import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MapPin,
    Phone,
    Mail,
    Globe,
    MoveLeft,
    CalendarDays,
    Users,
    Image as ImageIcon,
    Map as MapIcon,
    Grid3X3
} from 'lucide-react';
import './ClubsDashboard.css';
import '../../Clubs/ClubProfile.css';
import ShareMenuButton from '../../../components/share/ShareMenuButton';
import { clubsService, socialService } from '../../../services';
import { getClubPublicPath, getClubStorefrontPath } from '../../../utils/clubPublicPaths';

const getReadableError = (error, fallbackMessage) => {
    if (!error) return fallbackMessage;
    if (typeof error === 'string') return error;
    if (typeof error.message === 'string' && error.message.trim()) return error.message;
    if (typeof error.data === 'string' && error.data.trim()) return error.data;
    if (typeof error.data?.error === 'string' && error.data.error.trim()) return error.data.error;
    return fallbackMessage;
};

const formatEventDate = (value) => {
    if (!value) return 'Date TBC';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Date TBC';
    return date.toLocaleDateString();
};

const ClubView = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(1);
    const [isPublicFallback, setIsPublicFallback] = useState(false);
    const [claimForm, setClaimForm] = useState({
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        roleAtVenue: '',
        message: ''
    });
    const [claimSubmitting, setClaimSubmitting] = useState(false);
    const [claimFeedback, setClaimFeedback] = useState('');

    useEffect(() => {
        let isDisposed = false;

        const fetchClubDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                setClub(null);
                setIsPublicFallback(false);

                const data = await clubsService.getClub(clubId);
                if (isDisposed) return;
                setClub(data || null);
            } catch (err) {
                try {
                    const publicData = await clubsService.getPublicClubProfile(clubId);
                    if (isDisposed) return;
                    setClub(publicData || null);
                    setIsPublicFallback(true);
                    setError(null);
                } catch (fallbackError) {
                    if (isDisposed) return;
                    setError(
                        getReadableError(
                            fallbackError,
                            getReadableError(err, 'Unable to load this club profile right now.')
                        )
                    );
                }
            } finally {
                if (isDisposed) return;
                setLoading(false);
            }
        };

        if (clubId) {
            fetchClubDetails();
        }

        return () => {
            isDisposed = true;
        };
    }, [clubId]);

    if (loading) {
        return <div className="loading-container"><div className="loading-spinner"></div></div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <p className="error-message">{error}</p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <button className="btn btn-outline-secondary" onClick={() => navigate(getClubPublicPath(clubId))}>
                        Open Public Profile
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    if (!club) {
        return (
            <div className="error-container">
                <p className="error-message">This club profile is not available right now.</p>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    const hasPhone = Boolean(club.phoneNumber);
    const hasEmail = Boolean(club.email);
    const hasWebsite = Boolean(club.website);
    const clubProfileTier = (club?.profileTier || '').toLowerCase();
    const isPremiumVenueProfile = Boolean(club?.isPremiumVenue) || clubProfileTier === 'premium';
    const websiteUrl = hasWebsite
        ? (club.website.startsWith('http') ? club.website : `https://${club.website}`)
        : '';
    const brandPrimary = club?.primaryBrandColor || '#0f766e';
    const brandSecondary = club?.secondaryBrandColor || '#f59e0b';
    const storefrontUrl = getClubStorefrontPath(club);
    const profileLabel = isPremiumVenueProfile ? 'Premium Venue Profile' : 'Free Venue Profile';
    const primaryPhoto = club?.photos?.[0]?.photoUrl;
    const activeOffers = club?.offers || [];
    const upcomingEvents = club?.events || [];
    const facilityItems = [
        { label: 'Cafe', value: club?.hasCafe },
        { label: 'Shop', value: club?.hasShop },
        { label: 'Toilets', value: club?.hasToilets },
        { label: 'Car Park', value: club?.hasCarpark }
    ];

    const summaryCards = [
        { label: 'Stands', value: club.stands?.length || 0, icon: Grid3X3 },
        { label: 'Photos', value: club.photos?.length || 0, icon: ImageIcon },
        { label: 'Maps', value: club.maps?.length || 0, icon: MapIcon },
        { label: 'Events', value: club.events?.length || 0, icon: CalendarDays }
    ];

    const renderEmptyState = (title, description) => (
        <div className="dashboard-card">
            <h4 className='club-heading'>{title}</h4>
            <p className="club-describer">{description}</p>
        </div>
    );

    const handleClaimFormChange = (e) => {
        const { name, value } = e.target;
        setClaimForm(prev => ({ ...prev, [name]: value }));
    };

    const handleClaimSubmit = async (e) => {
        e.preventDefault();
        if (!claimForm.contactName.trim() || !claimForm.contactEmail.trim()) {
            setClaimFeedback('Please add your name and email so we can verify and contact you.');
            return;
        }

        try {
            setClaimSubmitting(true);
            setClaimFeedback('');
            const response = await clubsService.submitClubClaimRequest(clubId, {
                contactName: claimForm.contactName.trim(),
                contactEmail: claimForm.contactEmail.trim(),
                contactPhone: claimForm.contactPhone.trim() || null,
                roleAtVenue: claimForm.roleAtVenue.trim() || null,
                message: claimForm.message.trim() || null
            });

            setClaimFeedback(response?.message || 'Claim request submitted. Our team will contact you.');
            setClaimForm({
                contactName: '',
                contactEmail: '',
                contactPhone: '',
                roleAtVenue: '',
                message: ''
            });
        } catch (submitError) {
            setClaimFeedback(submitError?.message || 'Unable to submit claim request right now. Please try again.');
        } finally {
            setClaimSubmitting(false);
        }
    };

    const shareVenue = async (platform) => {
        try {
            const sharePlatform = platform === 'Copy' ? 'CopyLink' : platform;
            const data = await socialService.shareVenue(Number(clubId), sharePlatform);
            if (!data?.shareUrl) return;

            if (platform === 'Copy') {
                await navigator.clipboard.writeText(data.shareUrl);
                return;
            }

            window.open(data.shareUrl, '_blank', 'noopener,noreferrer,width=700,height=550');
        } catch (error) {
            console.error('Error sharing venue:', error);
        }
    };

    return (
        <div className="dashboard-component club-profile-page" style={{ background: '#f3f5f7', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="club-header" style={{ marginBottom: '24px' }}>
                <button className="dashboard-back-button" onClick={() => navigate(-1)}>
                    <MoveLeft className='me-2' /> Back to Venues
                </button>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}
                >
                    <div className="dashboard-heading" style={{ marginBottom: 0, flex: '1 1 420px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {club.logoUrl ? (
                                <img
                                    src={club.logoUrl}
                                    alt={`${club.name} logo`}
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        objectFit: 'cover',
                                        borderRadius: '16px',
                                        background: '#fff',
                                        padding: '6px',
                                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
                                    }}
                                />
                            ) : null}
                            <div>
                                <h1>{club.name}</h1>
                                {isPublicFallback && (
                                    <p className="club-describer mb-0">Showing public profile details while full venue data is unavailable.</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <ShareMenuButton onShare={shareVenue} align="left" />
                </div>
            </div>

            <section
                className={`club-profile-hero ${isPremiumVenueProfile ? 'premium' : 'free'}`}
                style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})`, marginBottom: '20px' }}
            >
                <div className="club-profile-hero-content">
                    <span className="club-profile-tier">{profileLabel}</span>
                    {club.logoUrl ? (
                        <img
                            src={club.logoUrl}
                            alt={`${club.name} logo`}
                            style={{
                                width: '82px',
                                height: '82px',
                                objectFit: 'cover',
                                borderRadius: '20px',
                                background: '#fff',
                                padding: '8px',
                                marginBottom: '16px'
                            }}
                        />
                    ) : null}
                    <h1>{club.name}</h1>
                    <p>{club.introduction || club.description || 'Venue details coming soon.'}</p>
                    <div className="club-profile-contact-row">
                        {club.phoneNumber ? <span>{club.phoneNumber}</span> : null}
                        {club.email ? <span>{club.email}</span> : null}
                        {websiteUrl ? (
                            <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                                Visit website
                            </a>
                        ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '18px' }}>
                        <a
                            href={getClubPublicPath(club)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '12px 18px',
                                borderRadius: '999px',
                                background: '#fff',
                                color: brandPrimary,
                                fontWeight: 700,
                                textDecoration: 'none'
                            }}
                        >
                            View Public Profile
                        </a>
                        {isPremiumVenueProfile ? (
                            <a
                                href={storefrontUrl}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '12px 18px',
                                    borderRadius: '999px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    textDecoration: 'none'
                                }}
                            >
                                Visit Shopfront
                            </a>
                        ) : null}
                    </div>
                </div>
                <div className="club-profile-hero-media">
                    {primaryPhoto ? (
                        <img src={primaryPhoto} alt={`${club.name} venue`} />
                    ) : (
                        <div className="club-profile-hero-placeholder">No hero image yet</div>
                    )}
                </div>
            </section>

            <section className="club-profile-stats">
                <article><strong>{activeOffers.length}</strong><span>Live Perks</span></article>
                <article><strong>{upcomingEvents.length}</strong><span>Upcoming Events</span></article>
                <article><strong>{club.photos?.length || 0}</strong><span>Gallery Photos</span></article>
                <article><strong>{summaryCards[0].value}</strong><span>Stands</span></article>
            </section>

            <div className="club-profile-content-layout">
                <div className="club-profile-content-main">
                    <section className="club-profile-card">
                        <div className="dashboard-tabs" style={{ marginTop: 0 }}>
                            <button className={`dashboard-tab ${tab === 1 && 'active'}`} onClick={() => setTab(1)}>About</button>
                            <button className={`dashboard-tab ${tab === 2 && 'active'}`} onClick={() => setTab(2)}>Stands</button>
                            <button className={`dashboard-tab ${tab === 3 && 'active'}`} onClick={() => setTab(3)}>Photos</button>
                            <button className={`dashboard-tab ${tab === 4 && 'active'}`} onClick={() => setTab(4)}>Maps</button>
                            <button className={`dashboard-tab ${tab === 5 && 'active'}`} onClick={() => setTab(5)}>Events</button>
                            <button className={`dashboard-tab ${tab === 6 && 'active'}`} onClick={() => setTab(6)}>Offers</button>
                        </div>
                    
                    {
                        tab === 1 && (
                            <>
                                {club.description && (
                                    <>
                                        <h4 className='club-heading'>Venue Overview</h4>
                                        <p className="club-describer">{club.description}</p>
                                    </>
                                )}
                                {club.introduction && (
                                    <>
                                        <h4 className='club-heading'>Why Visit</h4>
                                        <p className="club-describer">{club.introduction}</p>
                                    </>
                                )}
                                {!club.description && !club.introduction && renderEmptyState(
                                    'Profile details coming soon',
                                    'This venue has not added a fuller public description yet.'
                                )}
                            </>
                        )
                    }
                    {
                        tab === 2 && (
                            <div className="row">
                                {club.stands?.length ? club.stands.map((stand) => (
                                    <div key={stand.standId} className="col-md-6 dashboard-card-container">
                                        <div className="dashboard-card">
                                            {stand.photos?.[0] && (
                                                <img
                                                    src={stand.photos[0].photoUrl}
                                                    className="club-view-card-image"
                                                    alt=''
                                                />
                                            )}
                                            <div className="club-view-card-heading">
                                                <h2>{stand.name}</h2>
                                                <h6>{stand.standType}</h6>
                                            </div>
                                            <p className="club-view-card-describer">{stand.description}</p>
                                        </div>
                                    </div>
                                )) : <div className="col-12">{renderEmptyState('No stands published yet', 'Stand, peg, or lane details have not been added for this venue yet.')}</div>}
                            </div>
                        )
                    }
                    {
                        tab === 3 && (
                            <div className="row">
                                {club.photos?.length ? club.photos.map((photo) => (
                                    <div key={photo.clubPhotoId} className="col-md-6 dashboard-card-container">
                                        <div className="dashboard-card">
                                            <img
                                                src={photo.photoUrl}
                                                alt={photo.description || 'Club photo'}
                                                className="club-view-card-image"
                                            />
                                            {photo.description && <p className="club-view-card-describer">{photo.description}</p>}
                                        </div>
                                    </div>
                                )) : <div className="col-12">{renderEmptyState('No venue photos yet', 'This venue has not uploaded gallery images yet.')}</div>}
                            </div>
                        )
                    }
                    {
                        tab === 4 && (
                            <div className="row">
                                {club.maps?.length ? club.maps.map((map) => (
                                    <div key={map.clubMapId} className="col-md-6 dashboard-card-container">
                                        <div className="dashboard-card">
                                            <img
                                                src={map.mapUrl}
                                                alt={map.description || 'Club photo'}
                                                className="club-view-card-image"
                                            />
                                            {map.description && <p className="club-view-card-describer">{map.description}</p>}
                                        </div>
                                    </div>
                                )) : <div className="col-12">{renderEmptyState('No maps shared yet', 'Venue maps and layout guides have not been published yet.')}</div>}
                            </div>
                        )
                    }
                    {
                        tab === 5 && (
                            <div className="row">
                                {club.events?.length ? club.events.map((event) => (
                                    <div key={event.eventId} className="col-md-6 dashboard-card-container">
                                        <div className="dashboard-card">
                                            <div className="club-view-card-heading mt-0 mb-3">
                                                <h2>{event.title}</h2>
                                            </div>
                                            <div className="club-view-card-info">
                                                <CalendarDays size={16} className='club-view-card-info-icon' /> <span>{formatEventDate(event.eventDate)}</span>
                                            </div>
                                            {event.maxParticipants && (
                                                <div className="club-view-card-info">
                                                    <Users size={16} className='club-view-card-info-icon' /> <span>{event.maxParticipants}</span>
                                                </div>
                                            )}
                                            <p className="club-view-card-describer">{event.description}</p>
                                        </div>
                                    </div>
                                )) : <div className="col-12">{renderEmptyState('No upcoming events listed', 'This venue has not published any upcoming events yet.')}</div>}
                            </div>
                        )
                    }
                    {
                        tab === 6 && (
                            <div className="row">
                                {club.offers?.length ? club.offers.map((offer) => (
                                    <div key={offer.clubOfferId} className="col-md-6 dashboard-card-container">
                                        <div className="dashboard-card">
                                            <div className="club-view-card-heading mt-0 mb-3">
                                                <h2>{offer.title}</h2>
                                                <h6>{offer.offerType}</h6>
                                            </div>
                                            {offer.imageUrl ? (
                                                <img
                                                    src={offer.imageUrl}
                                                    alt={offer.title}
                                                    className="club-view-card-image"
                                                />
                                            ) : null}
                                            {offer.shortDescription && (
                                                <p className="club-view-card-describer">{offer.shortDescription}</p>
                                            )}
                                            {offer.offerCode && (
                                                <div className="club-view-card-info">
                                                    <Users size={16} className='club-view-card-info-icon' /> <span>Code: {offer.offerCode}</span>
                                                </div>
                                            )}
                                            {offer.endDate && (
                                                <div className="club-view-card-info">
                                                    <CalendarDays size={16} className='club-view-card-info-icon' /> <span>Ends {formatEventDate(offer.endDate)}</span>
                                                </div>
                                            )}
                                            {offer.fullDescription && <p className="club-view-card-describer">{offer.fullDescription}</p>}
                                        </div>
                                    </div>
                                )) : <div className="col-12">{renderEmptyState('No active offers yet', 'This venue has not published any active offers or perks yet.')}</div>}
                            </div>
                        )
                    }
                    </section>
                </div>
                <aside className="club-profile-content-side">
                    <article className="club-profile-card">
                        <h3>General Info</h3>
                        <div className="club-info-item">
                            <MapPin size={18} className='club-info-item-icon' />
                            <span className='club-info-item-text'>{club.address}</span>
                        </div>
                        {hasPhone && (
                            <div className="club-info-item">
                                <Phone size={18} className='club-info-item-icon' />
                                <a href={`tel:${club.phoneNumber}`} className='club-info-item-text'>{club.phoneNumber}</a>
                            </div>
                        )}
                        {hasEmail && (
                            <div className="club-info-item">
                                <Mail size={18} className='club-info-item-icon' />
                                <a href={`mailto:${club.email}`} className='club-info-item-text'>{club.email}</a>
                            </div>
                        )}
                        {hasWebsite && (
                            <div className="club-info-item">
                                <Globe size={18} className='club-info-item-icon' />
                                <a 
                                    href={websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className='club-info-item-text'
                                >
                                    {websiteUrl}
                                </a>
                            </div>
                        )}
                    </article>
                    <article className="club-profile-card">
                        <h3>Facilities</h3>
                        <div className="club-facility-grid">
                            {facilityItems.map((item) => (
                                <span key={item.label} className={item.value ? 'on' : 'off'}>
                                    {item.value ? 'Yes' : 'No'} {item.label}
                                </span>
                            ))}
                        </div>
                    </article>
                    <article className="club-profile-card profile-compare">
                        <h3>Venue Access</h3>
                        {!isPremiumVenueProfile ? (
                            <>
                                <p className='club-info-item-text mb-3'>
                                    This logged-in view already gives venue members a cleaner internal look at the profile. Upgrade to Premium to unlock the richer public presentation and storefront journey too.
                                </p>
                                <form onSubmit={handleClaimSubmit} className="club-claim-form">
                                    <input
                                        type="text"
                                        name="contactName"
                                        value={claimForm.contactName}
                                        onChange={handleClaimFormChange}
                                        placeholder="Your name"
                                        required
                                    />
                                    <input
                                        type="email"
                                        name="contactEmail"
                                        value={claimForm.contactEmail}
                                        onChange={handleClaimFormChange}
                                        placeholder="Work email"
                                        required
                                    />
                                    <input
                                        type="text"
                                        name="contactPhone"
                                        value={claimForm.contactPhone}
                                        onChange={handleClaimFormChange}
                                        placeholder="Phone (optional)"
                                    />
                                    <input
                                        type="text"
                                        name="roleAtVenue"
                                        value={claimForm.roleAtVenue}
                                        onChange={handleClaimFormChange}
                                        placeholder="Role at venue (optional)"
                                    />
                                    <textarea
                                        name="message"
                                        value={claimForm.message}
                                        onChange={handleClaimFormChange}
                                        placeholder="Anything we should know before we contact you?"
                                        rows={3}
                                    />
                                    <button className="dashboard-default-btn w-100" type="submit" disabled={claimSubmitting}>
                                        {claimSubmitting ? 'Submitting...' : 'Claim This Venue'}
                                    </button>
                                </form>
                                {claimFeedback && <p className="club-claim-feedback">{claimFeedback}</p>}
                            </>
                        ) : (
                            <p className='club-info-item-text mt-4'>
                                Premium venue features are enabled for this venue and are already being managed, including the public shopfront experience.
                            </p>
                        )}
                    </article>
                </aside>
            </div>

        </div>
    );
};

export default ClubView;
